import { ColumnDef } from '@tanstack/react-table';
import { keyBy, mapValues } from 'lodash';
import { TrashIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import { Autocomplete, ItemStatusEnum, useApiQuery } from '@/modules/common';
import { Mask } from '@/modules/masks/types';
import { OverlayItem } from '@/modules/overlays/types';

import { defaultMasks, MaskColumn } from '..';

export const useMasksTab = () => {
  const { control, watch } = useFormContext();
  const { isFetching, data: masks } = useApiQuery<Mask[]>('/masks', {
    queryKey: ['masks'],
    initialData: [],
  });
  const { data: overlays } = useApiQuery<OverlayItem[]>('/overlays', {
    queryKey: ['overlays'],
    initialData: [],
  });

  const filteredMasksData = useMemo(
    () => masks.filter((item) => item.status === ItemStatusEnum.Ready),
    [masks]
  );

  const filteredOverlaysData = useMemo(
    () => overlays.filter((item) => item.status === ItemStatusEnum.Ready),
    [overlays]
  );

  const formattedMasksData = useMemo((): Record<string, Mask> => {
    if (!masks) return {};

    return mapValues(keyBy(masks, 'id'));
  }, [masks]);

  const formattedOverlayData = useMemo((): Record<string, OverlayItem> => {
    if (!overlays) return {};

    return mapValues(keyBy(overlays, 'id'));
  }, [overlays]);

  const fields = watch('masks', defaultMasks);

  const { append, remove, update } = useFieldArray({
    control,
    name: 'masks',
  });

  const filledMasksId = fields?.map((style) => style.maskId).filter(Boolean);

  const maskOptions = filteredMasksData
    ?.filter((item) => !filledMasksId.includes(item.id))
    ?.map((style) => ({
      value: style.name,
      id: style.id,
    }));

  const overlaysOptions = filteredOverlaysData?.map((item) => ({
    value: item.name,
    id: item.id,
  }));

  const formattedMasks = fields.map((item) => {
    const formattedMask = formattedMasksData[item.maskId];
    const formattedOverlay = formattedOverlayData[item.overlayId];

    return {
      ...item,
      id: formattedMask?.id ?? '',
      name: formattedMask?.name ?? '',
      imagesPublicUrls: formattedMask?.imagesPublicUrls,
      overlay: formattedOverlay?.name,
      overlayImageUrl: formattedOverlay?.imagePublicUrl,
    };
  });

  const onEdit = (mask: MaskColumn, selectedMask: MaskColumn) => {
    const index = fields.findIndex(
      (field) => field.maskId === selectedMask?.id
    );

    if (index !== -1) {
      update(index, mask);
    }
  };

  const onAdd = (mask: MaskColumn) => {
    append(mask);
  };

  const columns: ColumnDef<MaskColumn>[] = [
    {
      size: 200,
      accessorKey: 'name',
      header: 'Mask Name',
      cell: ({ row }) => (
        <Autocomplete
          name={`masks.${row.index}.maskId`}
          control={control}
          searchable
          placeholder="Select mask"
          options={maskOptions}
          className="w-[200px] border-0"
          defaultValue={row.getValue('name')}
        />
      ),
    },
    {
      accessorKey: 'imagesPublicUrls',
      header: 'Preview',
      cell: ({ row }) => {
        const images = Object.values({
          large: row.original.imagesPublicUrls?.large,
          medium: row.original.imagesPublicUrls?.transparent,
        }).filter(Boolean);

        return (
          <div className="flex gap-2">
            {images.map((image, index) => (
              <img
                key={index}
                src={image}
                alt="Mask preview image"
                className="w-[30px] h-[34px] rounded-lg"
              />
            ))}
          </div>
        );
      },
    },
    {
      size: 200,
      accessorKey: 'overlay',
      header: 'Overlay name',
      cell: ({ row }) => (
        <Autocomplete
          name={`masks.${row.index}.overlayId`}
          control={control}
          searchable
          placeholder="Select overlay"
          options={overlaysOptions}
          className="w-[200px] border-0"
          defaultValue={row.getValue('overlay')}
        />
      ),
    },
    {
      accessorKey: 'overlayImageUrl',
      header: 'Overlay Preview',
      cell: ({ row }) => {
        return row.original.overlayImageUrl ? (
          <img
            src={row.original.overlayImageUrl}
            alt="Overlay preview image"
            className="w-[34px] h-[34px] rounded-lg object-cover"
          />
        ) : null;
      },
    },
    {
      size: 40,
      id: 'actions',
      header: 'Action',
      enableHiding: false,
      cell: ({ row }) => (
        <Button
          data-testid="remove-mask-button"
          variant="ghost"
          size="icon"
          onClick={() => remove(row.index)}
        >
          <TrashIcon size={28} color={colors.zinc[400]} />
        </Button>
      ),
    },
  ];

  return {
    formattedMasks,
    columns,
    fields,
    filteredMasksData,
    filteredOverlaysData,
    isFetching,
    control,
    onEdit,
    onAdd,
  };
};
